import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "@/components/shared/States";
import { Avatar, ScorePill } from "@/components/shared/Badges";
import { fmtMoney, fmtNum, timeAgo } from "@/lib/format";
import { StartMyDay } from "@/components/dashboard/StartMyDay";
import {
  TrendingUp, TrendingDown, Handshake, Target, Users, Percent, DollarSign, CheckCircle2,
  Sparkles, Flame, Lightbulb, AlertTriangle, ArrowRight, Mail, Calendar, FileText, Activity as ActivityIcon,
  MessageSquare, ListTodo, Bell, Rocket,
} from "lucide-react";

const KPI = ({ label, value, delta, icon: Icon, featured, testid }) => (
  <div data-testid={testid} className={`rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-[0_8px_24px_rgba(11,16,43,0.08)] transition-shadow ${featured ? "border-l-2 border-l-[#3B82F6]" : ""}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="h-7 w-7 rounded-lg bg-slate-50 flex items-center justify-center"><Icon className="h-4 w-4 text-[#7C3AED]" /></div>
    </div>
    <div className="text-2xl font-bold tabular-nums tracking-tight mt-2 text-slate-900">{value}</div>
    {delta != null && <div className={`text-xs font-semibold mt-1 flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>{delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{delta >= 0 ? "+" : ""}{delta}% vs last month</div>}
  </div>
);

const ACT_ICON = { email: Mail, meeting: Calendar, file: FileText, ai: Sparkles, call: Users, web: ActivityIcon, note: FileText, created: CheckCircle2, stage_changed: Handshake };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { askAi } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [focus, setFocus] = useState({ tasks: 0, messages: 0, alerts: 0 });

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.post("/ai/insights").then(({ data }) => setInsights(data.insights || [])).catch(() => setInsights([])).finally(() => setInsightsLoading(false));
    Promise.all([
      api.get("/tasks?status=open&limit=1").catch(() => ({ data: {} })),
      api.get("/messages/summary").catch(() => ({ data: {} })),
      api.get("/notifications").catch(() => ({ data: {} })),
    ]).then(([t, m, n]) => setFocus({ tasks: t.data?.total || 0, messages: m.data?.unread || 0, alerts: n.data?.unread || 0 }));
  }, []);

  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; })();

  if (!data) return <LoadingSkeleton />;
  const k = data.kpis;
  const funnel = data.funnel;
  const funnelMax = Math.max(funnel.leads, 1);
  const stages = [{ l: "Leads", v: funnel.leads, c: "#7C3AED" }, { l: "Qualified", v: funnel.qualified, c: "#3B82F6" }, { l: "Opportunities", v: funnel.opportunities, c: "#06B6D4" }, { l: "Won", v: funnel.won, c: "#10B981" }];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{greeting}, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-sm text-slate-500 mt-0.5">Here's what's happening across your revenue engine today.</p>
        </div>
        <StartMyDay />
      </div>

      {/* Growth Engine CTA — AI lead finding & outreach */}
      <div data-testid="growth-engine-cta" className="relative overflow-hidden rounded-2xl aurelo-gradient p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0"><Rocket className="h-5.5 w-5.5 text-white" /></div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white">Let AI find your next customers</div>
            <p className="text-xs text-white/80 mt-0.5">Describe who you sell to — Aurelo searches the web for real leads, researches them, and drafts outreach for your approval.</p>
          </div>
        </div>
        <Button data-testid="growth-engine-cta-button" onClick={() => navigate("/app/growth")} className="bg-white text-[#7C3AED] hover:bg-white/90 shrink-0">
          Open Growth Engine<ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>

      {/* Focus strip — quick access to tasks, messages & alerts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="dashboard-focus-strip">
        {[
          { key: "tasks", label: "Open Tasks", sub: "Due & upcoming", value: focus.tasks, icon: ListTodo, to: "/app/tasks", tint: "text-[#0EA5E9] bg-sky-50" },
          { key: "messages", label: "Unread Messages", sub: "Inbox & email threads", value: focus.messages, icon: MessageSquare, to: "/app/contacts", tint: "text-[#7C3AED] bg-violet-50" },
          { key: "alerts", label: "Alerts", sub: "Signals need attention", value: focus.alerts, icon: Bell, to: "/app/insights", tint: "text-[#EF4444] bg-red-50" },
        ].map((f) => (
          <button key={f.key} data-testid={`focus-${f.key}`} onClick={() => navigate(f.to)}
            className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 text-left hover:shadow-[0_8px_24px_rgba(11,16,43,0.08)] hover:-translate-y-0.5 transition-all">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${f.tint}`}><f.icon className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className="text-2xl font-bold tabular-nums text-slate-900">{f.value}</span><span className="text-sm font-medium text-slate-700">{f.label}</span></div>
              <div className="text-xs text-slate-400">{f.sub}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="pipeline-value-kpi" label="Pipeline Value" value={fmtMoney(k.pipeline_value)} delta={18} icon={DollarSign} featured />
        <KPI testid="open-deals-kpi" label="Open Deals" value={fmtNum(k.open_deals)} delta={12} icon={Handshake} />
        <KPI testid="new-leads-kpi" label="New Leads" value={fmtNum(k.new_leads)} delta={24} icon={Target} />
        <KPI testid="conversion-kpi" label="Conversion Rate" value={`${k.conversion_rate}%`} delta={6} icon={Percent} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Revenue (Won)" value={fmtMoney(k.revenue)} icon={DollarSign} />
        <KPI label="Weighted Pipeline" value={fmtMoney(k.weighted_pipeline)} icon={TrendingUp} />
        <KPI label="Contacts" value={fmtNum(k.contacts)} icon={Users} />
        <KPI label="Won Deals" value={fmtNum(k.won_deals)} icon={CheckCircle2} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Pipeline funnel */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-slate-900">Sales Pipeline</h3><Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/app/pipeline")}>View <ArrowRight className="h-3 w-3 ml-1" /></Button></div>
          <div className="space-y-3">
            {stages.map((s) => (
              <div key={s.l}>
                <div className="flex justify-between text-xs mb-1"><span className="text-slate-600 font-medium">{s.l}</span><span className="tabular-nums font-semibold text-slate-900">{s.v}</span></div>
                <div className="h-2.5 rounded-full bg-slate-100"><div className="h-2.5 rounded-full transition-all" style={{ width: `${Math.max((s.v / funnelMax) * 100, 4)}%`, background: s.c }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insights */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 lg:col-span-2" data-testid="ai-insights-card">
          <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-[#7C3AED] flex items-center gap-1.5"><Sparkles className="h-4 w-4" />AI Insights</h3><Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/app/insights")}>View All Insights <ArrowRight className="h-3 w-3 ml-1" /></Button></div>
          {insightsLoading ? (
            <div className="space-y-2 animate-pulse">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100" />)}</div>
          ) : (
            <div className="space-y-2">
              {(insights || []).slice(0, 4).map((ins, i) => {
                const Icon = ins.type === "risk" ? AlertTriangle : ins.type === "signal" ? Lightbulb : ins.type === "followup" ? Flame : Sparkles;
                const tint = ins.type === "risk" ? "text-red-500 bg-red-50" : ins.type === "signal" ? "text-amber-500 bg-amber-50" : ins.type === "followup" ? "text-orange-500 bg-orange-50" : "text-violet-500 bg-violet-50";
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50 transition-colors">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tint}`}><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">{ins.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{ins.detail}</div>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-400 shrink-0">{ins.impact}</span>
                  </div>
                );
              })}
              {(!insights || insights.length === 0) && <div className="text-sm text-slate-400 py-4">No insights available right now.</div>}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Top opportunities */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Top Opportunities</h3>
          <div className="space-y-2">
            {data.top_opportunities.map((d) => (
              <div key={d.id} onClick={() => navigate(`/app/deals/${d.id}`)} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50 cursor-pointer transition-colors">
                <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{(d.company_name || "?")[0]}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div><div className="text-xs text-slate-500">{d.stage} · {d.owner_name}</div></div>
                <div className="text-right"><div className="text-sm font-bold tabular-nums text-slate-900">{fmtMoney(d.value)}</div><div className="text-xs text-slate-400">{d.probability}%</div></div>
                <div className="w-16 hidden sm:block"><div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full aurelo-gradient" style={{ width: `${d.probability}%` }} /></div></div>
              </div>
            ))}
          </div>
        </div>

        {/* Today's tasks */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-semibold text-slate-900">Today's Tasks</h3><Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate("/app/tasks")}>All</Button></div>
          <div className="space-y-2">
            {data.today_tasks.length === 0 && <div className="text-sm text-slate-400 py-4">No tasks for today.</div>}
            {data.today_tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5 text-sm py-1.5">
                <span className={`h-2 w-2 rounded-full shrink-0 ${t.priority === "Critical" ? "bg-red-500" : t.priority === "High" ? "bg-amber-500" : "bg-slate-300"}`} />
                <span className="flex-1 truncate text-slate-700">{t.title}</span>
                <span className="text-[11px] text-slate-400 capitalize">{t.type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Recent Activity</h3>
        <div className="space-y-1">
          {data.recent_activity.slice(0, 8).map((a) => {
            const Icon = ACT_ICON[a.kind] || ActivityIcon;
            return (
              <div key={a.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                <div className="h-8 w-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-500" /></div>
                <span className="flex-1 text-sm text-slate-700 truncate">{a.description}</span>
                <span className="text-xs text-slate-400 shrink-0">{timeAgo(a.created_at)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
